/**
 * Resposta de quem não sabe o dado ("não sei", "n/a", "?", "xxx"): sairia escrita no documento
 * que vai para assinatura (decisão de 23/10). A mesma regra está no back
 * (`app/schemas/estatuto_digitacao.py`, `vaga`): mudou uma, muda a outra.
 *
 * Sobre o texto em minúsculas, sem acento e sem nada além de letra e número nas pontas: valor
 * inteiro, começo do valor ou em qualquer ponto dele ("Rua 10, nº não sei").
 */
const ESPACO =
  '\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff';
const INTEIRA = new RegExp(
  '^(nao|n|n/a|n\\.a|n/d|n\\.d|n/i|n/c|s/i|nenhuma?|nada|desconhecid[oa]|ignorad[oa]' +
    '|indefinid[oa]|pendente|duvida|em aberto|idem|[oa] mesm[oa]|teste|asdf)$',
);
const NO_COMECO = /^(nao (tenho|tem|possui|possuo|ha)|sem (rg|cpf|profissao|dados?)|perguntar)\b/;
const EM_QUALQUER_PONTO = new RegExp(
  '\\b(n(ao)? sei|nem sei|sei (la|nao)|nao (me )?lembro|nao sabe(mos)?|nao (informad|declarad)[oa]s?' +
    '|nao informou|nao consta|nao se aplica|a (definir|confirmar|preencher|verificar|combinar|informar)' +
    '|sem informac(ao|oes)|(preencher|ver|verificar|confirmar) depois|desconheco|esqueci' +
    '|(faco|fazemos|tenho) (a menor )?ideia|sem ideia|mesmo endereco|igual (acima|ao|a)' +
    '|fulano|beltrano|ciclano|sicrano)\\b',
);
// "?" solto no meio ("Rua 10, nº ?"); o "?" de link ("/?igsh=") não conta.
const INTERROGACAO = new RegExp(`(^|[${ESPACO}])\\?+(?=[${ESPACO},]|$)`);
const ESPACOS = new RegExp(`[${ESPACO}]+`);

export const MSG_VAGA = 'Informe o dado: o que estiver aqui sai escrito no documento.';

// Letra (tem maiúscula e minúscula) ou número: sem nenhum, é só pontuação ("?", "-", "...").
const temLetraOuNumero = (s: string) =>
  Array.from(s).some(c => '0123456789'.includes(c) || c.toLowerCase() !== c.toUpperCase());

/** `xVale`: "X" é valor (turma em romano, a rede X no canal). */
export const respostaVaga = (v: unknown, xVale = false): boolean => {
  if (typeof v !== 'string') return false;
  const s = v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(ESPACOS)
    .filter(Boolean)
    .join(' ');
  if (!s) return false;
  if (!temLetraOuNumero(v)) return true;
  if (INTERROGACAO.test(s)) return true;
  const t = s.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
  const soSinais = t.replace(/[ ./-]/g, '');
  // Com `xVale`, só X, XX e XXX (romano: turma 10/20/30, a rede X): "xxxx" segue vago.
  if (/^0+$/.test(soSinais) || (xVale ? /^x{4,}$/ : /^x+$/).test(soSinais)) return true;
  return INTEIRA.test(t) || NO_COMECO.test(t) || EM_QUALQUER_PONTO.test(t);
};

// O back confere depois de tirar o prefixo que o texto fixo já diz ("Curso de N/A" -> "N/A":
// `curso`, `sem_universidade` e `sem_faculdade_de`): aqui, com e sem ele.
const PREFIXOS: Record<string, RegExp[]> = {
  nome_curso: [/^curso\s+(?:de|em)\s+/i],
  nome_instituicao: [/^universidade\s+/i, /^(?:[Ff]aculdade\s+)?(?:de|do|da)\s+/],
};

export const respostaVagaNoCampo = (campo: string, v: unknown, xVale = false): boolean => {
  if (respostaVaga(v, xVale)) return true;
  if (typeof v !== 'string') return false;
  return (PREFIXOS[campo] ?? []).some(re => {
    const resto = v.trim().replace(re, '');
    return resto !== v.trim() && respostaVaga(resto, xVale);
  });
};
